---
title: "Duplicate candidates and merging"
description: "How Chosen detects duplicate candidates by resume file, why the same person can end up as two records, and how to spot and merge duplicates manually."
category: "candidates"
keywords:
  - "duplicate candidates"
  - "merge candidates"
  - "candidate deduplication"
  - "duplicate applicant ATS"
schema: "HowTo"
nav:
  section: "guides"
  order: 70
  parent: "/guides/candidates"
---

Chosen deduplicates candidates by the resume file itself. Upload a resume it has already seen and it reuses the existing candidate instead of making a new one. This catches the common case automatically — and misses a predictable one, which you merge by hand.

## How automatic dedup works

When a resume is uploaded, Chosen takes a fingerprint of the file's exact contents and checks it against the resumes already in your organization. Same fingerprint, same candidate — the upload links to the existing record rather than creating a duplicate. This happens silently; there's no prompt.

Because the fingerprint is of the **file**, the rule comes down to one thing:

- **Same person, same resume file** → one candidate. They apply to a second job with the resume they used before, and Chosen recognizes it. The existing candidate is linked to the new job.
- **Same person, a different or updated resume** → two candidates. A re-exported PDF, a fresh version with a new role added, the same content saved from a different program — any of these is a different file, so Chosen sees a new person.

That second case is the one to watch. It isn't a bug — Chosen can't tell that two different files describe the same human — but it does mean genuine duplicates show up, and you'll want to merge them.

## Spotting duplicates

The [candidate table](/guides/candidates/table-and-boards) is where duplicates surface. Sort or filter by name and a person who applied twice with different files sits as two rows — often two slightly different job titles, or two different application dates. A free-text search for a name you suspect is the fastest check.

## Merging duplicates

When you've found two records for one person, merge them from the candidate's detail panel.

<Steps>
  <li>Open one of the duplicate candidates.</li>
  <li>Choose to merge, and pick the other record as the duplicate.</li>
  <li>Confirm. The two become one.</li>
</Steps>

A merge keeps the candidate's history rather than discarding half of it: the job applications, the timeline of status changes, the resumes, and the notes from both records end up on the surviving candidate. The goal is one complete person, not the newer of two thin ones.

<Callout type="warning" title="Merging is not an undo button">
A merge collapses two records into one and can't be cleanly split back apart afterward. Before you confirm, make sure the two are actually the same person — a shared common name is not enough. When in doubt, open both and compare the work history.
</Callout>

## Limits

Automatic dedup matches on the resume file and nothing else. It won't catch the same person across two different files, and it has nothing to match on for a [manually entered](/guides/candidates/bulk-import-and-export) candidate with no resume at all. Two people who genuinely share a name are not duplicates — don't merge them. Deduplication is per organization: it looks across all your jobs, so a candidate already on file for one role is recognized when they apply to another.
